import { useState, useMemo } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  AlertCircle,
  Download,
  Calendar,
  Sparkles,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import Page from '@/components/page/Page';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/utils/formatters';

import {
  mockPurchaseOrders,
  mockSuppliers,
  mockItems,
  type PurchaseOrder,
} from '../data/mockData';

type TimeRange = 'mtd' | 'qtd' | 'ytd' | 'custom';

interface CategorySpend {
  name: string;
  value: number;
  percentage: number;
  change: number;
}

interface SupplierSpend {
  id: string;
  name: string;
  spending: number;
  orders: number;
  avgOrder: number;
  change: number;
  savings: number;
}

interface AIInsight {
  type: 'savings' | 'risk' | 'trend';
  title: string;
  description: string;
  impact: string;
  action: string;
}

const CHART_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--primary) / 0.8)',
  'hsl(var(--primary) / 0.6)',
  'hsl(var(--primary) / 0.4)',
  'hsl(var(--primary) / 0.25)',
  'hsl(var(--primary) / 0.15)',
];

const TIME_RANGES: { value: TimeRange; label: string }[] = [
  { value: 'mtd', label: 'MTD' },
  { value: 'qtd', label: 'QTD' },
  { value: 'ytd', label: 'YTD' },
  { value: 'custom', label: 'Custom' },
];

export function SpendingAnalyticsPage() {
  const [timeRange, setTimeRange] = useState<TimeRange>('ytd');
  const [showAIInsights, setShowAIInsights] = useState(false);

  // Calculate spending data from purchase orders
  const spendingData = useMemo(() => {
    const receivedOrders = mockPurchaseOrders.filter(
      (po) => po.status === 'received',
    );

    // Monthly spending data (generate 12 months)
    const monthlySpendData = Array.from({ length: 12 }, (_, i) => {
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      // Simulate monthly spending with some variation based on actual orders
      const baseSpend = 85000 + Math.random() * 30000;
      const orderContribution =
        receivedOrders.reduce((sum, po) => sum + po.totalAmount, 0) / 12;
      return {
        month: monthNames[i],
        spending: Math.round(baseSpend + orderContribution * 0.3),
        budget: 100000,
      };
    });

    // Category spending based on items in purchase orders
    const categoryTotals: Record<string, number> = {
      'raw-material': 0,
      component: 0,
      packaging: 0,
    };

    mockPurchaseOrders.forEach((po) => {
      po.items.forEach((poItem) => {
        const item = mockItems.find((i) => i.id === poItem.itemId);
        if (item) {
          categoryTotals[item.category] += poItem.totalPrice;
        }
      });
    });

    const totalCategorySpend = Object.values(categoryTotals).reduce(
      (a, b) => a + b,
      0,
    );

    const categorySpendData: CategorySpend[] = [
      {
        name: 'Raw Materials',
        value: categoryTotals['raw-material'] || 180000,
        percentage: Math.round(
          ((categoryTotals['raw-material'] || 180000) / totalCategorySpend) *
            100,
        ),
        change: -3.2,
      },
      {
        name: 'Components',
        value: categoryTotals['component'] || 95000,
        percentage: Math.round(
          ((categoryTotals['component'] || 95000) / totalCategorySpend) * 100,
        ),
        change: 5.1,
      },
      {
        name: 'Packaging',
        value: categoryTotals['packaging'] || 25000,
        percentage: Math.round(
          ((categoryTotals['packaging'] || 25000) / totalCategorySpend) * 100,
        ),
        change: -1.8,
      },
    ];

    // Supplier spending
    const supplierSpendMap: Record<
      string,
      { spending: number; orders: number }
    > = {};

    mockPurchaseOrders.forEach((po) => {
      if (!supplierSpendMap[po.supplierId]) {
        supplierSpendMap[po.supplierId] = { spending: 0, orders: 0 };
      }
      supplierSpendMap[po.supplierId].spending += po.totalAmount;
      supplierSpendMap[po.supplierId].orders += 1;
    });

    const topSuppliers: SupplierSpend[] = Object.entries(supplierSpendMap)
      .map(([supplierId, data]) => {
        const supplier = mockSuppliers.find((s) => s.id === supplierId);
        return {
          id: supplierId,
          name: supplier?.name || 'Unknown Supplier',
          spending: data.spending,
          orders: data.orders,
          avgOrder: Math.round(data.spending / data.orders),
          change: (Math.random() - 0.5) * 10,
          savings: Math.round(data.spending * 0.02),
        };
      })
      .sort((a, b) => b.spending - a.spending)
      .slice(0, 5);

    // Weekly spending trend
    const spendingTrendData = Array.from({ length: 8 }, (_, i) => ({
      week: `Week ${i + 1}`,
      current: Math.round(20000 + Math.random() * 15000),
      previous: Math.round(20000 + Math.random() * 15000),
    }));

    return {
      monthlySpendData,
      categorySpendData,
      topSuppliers,
      spendingTrendData,
      totalSpend: mockPurchaseOrders.reduce(
        (sum, po) => sum + po.totalAmount,
        0,
      ),
      totalSavings: topSuppliers.reduce((sum, s) => sum + s.savings, 0),
      avgMonthlySpend:
        monthlySpendData.reduce((sum, m) => sum + m.spending, 0) /
        monthlySpendData.length,
    };
  }, []);

  const budgetUtilization = (
    (spendingData.avgMonthlySpend / 100000) *
    100
  ).toFixed(1);

  const aiInsights: AIInsight[] = [
    {
      type: 'savings',
      title: 'Price Optimization Opportunity',
      description:
        'Silicon Wafer pricing from Taiwan Semiconductor is 8% higher than market average. Negotiating could save $45K annually.',
      impact: '$45,000/year',
      action: 'Renegotiate Contract',
    },
    {
      type: 'risk',
      title: 'Supplier Concentration Risk',
      description:
        'Top 3 suppliers account for 68% of total spend. Diversification recommended to reduce supply chain risk.',
      impact: 'High Risk',
      action: 'Diversify Suppliers',
    },
    {
      type: 'trend',
      title: 'Component Spend Increasing',
      description:
        'Components category spending up 15% over last quarter. Review forecast to ensure budget alignment.',
      impact: '+15% QoQ',
      action: 'Review Forecast',
    },
    {
      type: 'savings',
      title: 'Bulk Order Opportunity',
      description:
        'Consolidating Copper Substrate orders could unlock 12% volume discount from suppliers.',
      impact: '$12,400/year',
      action: 'Consolidate Orders',
    },
  ];

  return (
    <Page
      title="Spending Analytics"
      subtitle="Track procurement spending and identify cost-saving opportunities"
    >
      <div className="flex flex-col gap-6">
        {/* Header Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <div className="flex gap-2">
              {TIME_RANGES.map((range) => (
                <Button
                  key={range.value}
                  variant={timeRange === range.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTimeRange(range.value)}
                >
                  {range.label}
                </Button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant={showAIInsights ? 'default' : 'outline'}
              onClick={() => setShowAIInsights(!showAIInsights)}
              className="gap-2"
            >
              <Sparkles className="w-4 h-4" />
              AI Insights
            </Button>
            <Button variant="outline" className="gap-2">
              <Download className="w-4 h-4" />
              Export Report
            </Button>
          </div>
        </div>

        {/* AI Insights Panel */}
        {showAIInsights && (
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-6">
            <div className="flex items-start gap-3 mb-4">
              <Sparkles className="w-6 h-6 text-primary mt-1" />
              <div>
                <h2 className="text-xl font-semibold mb-1">
                  AI-Powered Insights
                </h2>
                <p className="text-sm text-muted-foreground">
                  Recommendations based on spending patterns and market data
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {aiInsights.map((insight, idx) => (
                <Card key={idx} className="p-4 border-primary/20">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold">{insight.title}</h3>
                    <Badge
                      variant={
                        insight.type === 'savings'
                          ? 'default'
                          : insight.type === 'risk'
                            ? 'destructive'
                            : 'secondary'
                      }
                      className={
                        insight.type === 'savings'
                          ? 'bg-green-100 text-green-700 hover:bg-green-100'
                          : ''
                      }
                    >
                      {insight.impact}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    {insight.description}
                  </p>
                  <Button variant="link" className="p-0 h-auto text-primary">
                    {insight.action} →
                  </Button>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-muted-foreground">
                Total Spend YTD
              </div>
              <DollarSign className="w-5 h-5 text-muted-foreground" />
            </div>
            <div className="text-2xl font-semibold mb-1">
              {formatCurrency(spendingData.totalSpend, {
                locale: 'en-US',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </div>
            <div className="flex items-center gap-1 text-xs text-red-600">
              <TrendingUp className="w-3 h-3" />
              <span>2.3% vs last year</span>
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-muted-foreground">
                Avg Monthly Spend
              </div>
              <DollarSign className="w-5 h-5 text-muted-foreground" />
            </div>
            <div className="text-2xl font-semibold mb-1">
              {formatCurrency(spendingData.avgMonthlySpend, {
                locale: 'en-US',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </div>
            <div className="text-xs text-muted-foreground">
              Budget utilization: {budgetUtilization}%
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-muted-foreground">
                Cost Savings YTD
              </div>
              <TrendingDown className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-2xl font-semibold text-green-600 mb-1">
              {formatCurrency(spendingData.totalSavings, {
                locale: 'en-US',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </div>
            <div className="flex items-center gap-1 text-xs text-green-600">
              <TrendingDown className="w-3 h-3" />
              <span>5.2% cost reduction</span>
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-muted-foreground">
                Active Suppliers
              </div>
              <AlertCircle className="w-5 h-5 text-muted-foreground" />
            </div>
            <div className="text-2xl font-semibold mb-1">
              {mockSuppliers.filter((s) => s.status === 'active').length}
            </div>
            <div className="text-xs text-muted-foreground">
              Top 3: 68% of spend
            </div>
          </Card>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Spending Trend */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">
              Monthly Spending vs Budget
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={spendingData.monthlySpendData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-border"
                />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                />
                <Tooltip
                  formatter={(value: number) =>
                    formatCurrency(value, {
                      locale: 'en-US',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })
                  }
                  contentStyle={{
                    borderRadius: '8px',
                    border: '1px solid hsl(var(--border))',
                    backgroundColor: 'hsl(var(--background))',
                  }}
                />
                <Legend />
                <Bar dataKey="budget" fill="hsl(var(--muted))" name="Budget" />
                <Bar
                  dataKey="spending"
                  fill="hsl(var(--primary))"
                  name="Actual Spending"
                />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {/* Category Breakdown */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Spending by Category</h3>
            <div className="flex items-center gap-6">
              <div className="flex-shrink-0">
                <ResponsiveContainer width={200} height={200}>
                  <PieChart>
                    <Pie
                      data={spendingData.categorySpendData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {spendingData.categorySpendData.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number) =>
                        formatCurrency(value, {
                          locale: 'en-US',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })
                      }
                      contentStyle={{
                        borderRadius: '8px',
                        border: '1px solid hsl(var(--border))',
                        backgroundColor: 'hsl(var(--background))',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-3">
                {spendingData.categorySpendData.map((category, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded"
                        style={{
                          backgroundColor:
                            CHART_COLORS[idx % CHART_COLORS.length],
                        }}
                      />
                      <span className="text-sm">{category.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold">
                        {formatCurrency(category.value, {
                          locale: 'en-US',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </div>
                      <div
                        className={`text-xs ${category.change < 0 ? 'text-green-600' : 'text-red-600'}`}
                      >
                        {category.change > 0 ? '+' : ''}
                        {category.change}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Spending Trend Comparison */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">
              Spending Trend Comparison
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={spendingData.spendingTrendData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-border"
                />
                <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                />
                <Tooltip
                  formatter={(value: number) =>
                    formatCurrency(value, {
                      locale: 'en-US',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })
                  }
                  contentStyle={{
                    borderRadius: '8px',
                    border: '1px solid hsl(var(--border))',
                    backgroundColor: 'hsl(var(--background))',
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="current"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  name="Current Period"
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="previous"
                  stroke="hsl(var(--muted-foreground))"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  name="Previous Period"
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          {/* Top Suppliers Table */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">
              Top Suppliers by Spend
            </h3>
            <div className="space-y-3">
              {spendingData.topSuppliers.map((supplier, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium">
                        {supplier.name}
                      </span>
                      <span
                        className={`text-xs ${supplier.change < 0 ? 'text-green-600' : 'text-red-600'}`}
                      >
                        {supplier.change > 0 ? '+' : ''}
                        {supplier.change.toFixed(1)}%
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {supplier.orders} orders • Avg:{' '}
                      {formatCurrency(supplier.avgOrder, {
                        locale: 'en-US',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                      /order
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold">
                      {formatCurrency(supplier.spending, {
                        locale: 'en-US',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </div>
                    <div className="text-xs text-green-600">
                      Saved{' '}
                      {formatCurrency(supplier.savings, {
                        locale: 'en-US',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Detailed Spending Table */}
        <Card>
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold">Category Spending Details</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Total Spend
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    % of Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Change YoY
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Top Supplier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {spendingData.categorySpendData.map((category, idx) => (
                  <tr key={idx} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium">
                      {category.name}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {formatCurrency(category.value, {
                        locale: 'en-US',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {category.percentage}%
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`text-sm font-medium ${category.change < 0 ? 'text-green-600' : 'text-red-600'}`}
                      >
                        {category.change > 0 ? '+' : ''}
                        {category.change}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {spendingData.topSuppliers[idx]?.name || 'Various'}
                    </td>
                    <td className="px-6 py-4">
                      <Button variant="link" className="p-0 h-auto">
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Page>
  );
}
